import { StoreGetProductsParams } from "@medusajs/medusa"
import { useCollections } from "medusa-react"
import { ChangeEvent, useState } from "react"
import { getProductFilters, ProductFilter } from "@lib/util/get-product-filters"
import { useQuery } from "react-query"
import { collectionKeys } from "medusa-react/src/hooks/store/collections/queries"
import { RefinementListProps } from "@lib/util/filter-types"
import clsx from "clsx"
import { Transition } from "@headlessui/react"
import { FormattedMessage } from "react-intl"
import { BsCheck } from "react-icons/bs"

const RefinementList = ({
    refinementList,
    setRefinementList,
    showCollection = true,
    collectionId,
}: RefinementListProps) => {
    const [filterDropdown, setFilterDropdown] = useState<string>("")
    const { collections, isLoading } = useCollections()
    const filterData = useQuery(["get_product_filters", collectionId], () =>
        getProductFilters(collectionId)
    )

    const handleCollectionChange = (
        e: ChangeEvent<HTMLInputElement>,
        id: string
    ) => {
        const { checked } = e.target

        const collectionIds = refinementList.collection_id || []

        const exists = collectionIds.includes(id)

        if (checked && !exists) {
            setRefinementList({
                ...refinementList,
                collection_id: [...collectionIds, id],
            })

            return
        }

        if (!checked && exists) {
            setRefinementList({
                ...refinementList,
                collection_id: collectionIds.filter((c) => c !== id),
            })

            return
        }

        return
    }
    const handleFilterChange = (
        e: ChangeEvent<HTMLInputElement>,
        opt_id: string,
        values: string[]
    ) => {
        const { checked } = e.target

        const filters = refinementList.filters || {}
        const exists = Object.keys(filters).includes(opt_id)

        if (checked && !exists) {
            setRefinementList({
                ...refinementList,
                filters: { ...filters, [opt_id]: values },
            })
            return
        }

        if (!checked && exists) {
            const newFilters = { ...filters }
            delete newFilters[opt_id]
            setRefinementList({
                ...refinementList,
                filters: newFilters,
            })

            return
        }

        return
    }

    return (
        <div
            className={clsx(
                `small:sticky small:top-[100px] py-4 flex gap-y-2 text-darkTone px-0 flex-col w-full flex-1 `
            )}
        >
            <div
                className={
                    "relative flex small:flex-col px-3 pb-2 w-auto text-2xl font-semibold font-display items-start subpixel-antialiased"
                }
            >
                Filters
            </div>
            {showCollection && (
                <div
                    key={"collecties-filter"}
                    className={clsx(
                        `relative flex gap-x-3 z-20 flex-col small:gap-y-3 px-3 w-full border-b-2 border-lightTone pb-2`
                    )}
                >
                    <button
                        onClick={() =>
                            setFilterDropdown(
                                filterDropdown === "collecties"
                                    ? ""
                                    : "collecties"
                            )
                        }
                        className="flex flex-row justify-between font-semibold text-lg font-display w-full pr-4 h-[30px] md:h-[38px] text-darkTone border-0 focus:outline-none items-center"
                    >
                        <span className="select-none">
                            Collecties
                            {(refinementList?.collection_id?.length ?? 0) >
                                0 && (
                                <span
                                    className={
                                        "pl-2 text-xs font-normal text-neutral-500"
                                    }
                                >
                                    ({refinementList.collection_id?.length}{" "}
                                    geselecteerd)
                                </span>
                            )}
                        </span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={clsx(
                                `w-6 h-6 stroke-current stroke-1 transition-all duration-300`,
                                {
                                    "-scale-100":
                                        filterDropdown === "collecties",
                                }
                            )}
                            viewBox="0 0 384 512"
                        >
                            <path d="M191.1 352c-1.1 0-3.969-.7344-5.531-2.219L34.47 205.8c-3.187-3.047-3.312-8.125-.25-11.31c3.094-3.188 8.156-3.281 11.31-.25l146.5 138.7l146.5-138.7c3.219-3.031 8.281-2.938 11.31 .25c3.062 3.188 2.937 8.266-.25 11.31l-151.1 144C195.1 351.3 193.1 352 191.1 352z" />
                        </svg>
                    </button>
                    <Transition
                        show={filterDropdown === "collecties"}
                        as="ul"
                        unmount={false}
                        className={clsx(
                            `relative z-10 py-2 transition-all origin-top duration-500 overflow-hidden w-full`,
                            `text-base-regular flex flex-col items-left px-4 gap-x-4 small:gap-y-2`,
                            `border-l border-lightTone`
                        )}
                        enterFrom="transform scale-95 opacity-0 max-h-0"
                        enterTo="transform scale-100 opacity-100 max-h-[1000px]"
                        leaveFrom="transform scale-100 opacity-100 max-h-[1000px]"
                        leaveTo="transform scale-95 opacity-0 max-h-0"
                    >
                        {collections?.map((c) => (
                            <li key={`collectie-${c.id}`}>
                                <label
                                    className={clsx(
                                        "flex items-center gap-x-2 cursor-pointer",
                                        {
                                            "text-accent":
                                                refinementList.collection_id?.includes(
                                                    c.id
                                                ),
                                        }
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        defaultChecked={refinementList.collection_id?.includes(
                                            c.id
                                        )}
                                        onChange={(e) =>
                                            handleCollectionChange(e, c.id)
                                        }
                                        className={clsx(
                                            "accent-amber-200 hidden"
                                        )}
                                    />
                                    {c.title}
                                    <BsCheck
                                        size={24}
                                        className={clsx("absolute right-3", {
                                            " hidden":
                                                !refinementList.collection_id?.includes(
                                                    c.id
                                                ),
                                        })}
                                    />
                                </label>
                            </li>
                        ))}
                    </Transition>
                </div>
            )}
            {typeof filterData.data !== "undefined" &&
                Object.keys(filterData.data).map((key) => (
                    <div
                        key={key}
                        className={clsx(
                            `relative flex gap-x-3 z-20 flex-col small:gap-y-3 px-3 w-full border-b-2 border-lightTone pb-2`
                        )}
                    >
                        <button
                            onClick={() =>
                                setFilterDropdown(
                                    filterDropdown === key ? "" : key
                                )
                            }
                            className="flex flex-row justify-between font-semibold text-lg font-display w-full pr-4 h-[30px] md:h-[38px] text-darkTone border-0 focus:outline-none items-center"
                        >
                            <span className="select-none">{key}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={clsx(
                                    `w-6 h-6 stroke-current stroke-1 transition-all duration-300`,
                                    { "-scale-100": filterDropdown === key }
                                )}
                                viewBox="0 0 384 512"
                            >
                                <path d="M191.1 352c-1.1 0-3.969-.7344-5.531-2.219L34.47 205.8c-3.187-3.047-3.312-8.125-.25-11.31c3.094-3.188 8.156-3.281 11.31-.25l146.5 138.7l146.5-138.7c3.219-3.031 8.281-2.938 11.31 .25c3.062 3.188 2.937 8.266-.25 11.31l-151.1 144C195.1 351.3 193.1 352 191.1 352z" />
                            </svg>
                        </button>
                        <Transition
                            show={filterDropdown === key}
                            as="ul"
                            unmount={false}
                            className={clsx(
                                `relative z-10 py-2 transition-all origin-top duration-500 overflow-hidden w-full`,
                                `text-base-regular flex flex-col items-left px-4 gap-x-4 small:gap-y-2`,
                                `border-l border-lightTone`
                            )}
                            enterFrom="transform scale-95 opacity-0 max-h-0"
                            enterTo="transform scale-100 opacity-100 max-h-[1000px]"
                            leaveFrom="transform scale-100 opacity-100 max-h-[1000px]"
                            leaveTo="transform scale-95 opacity-0 max-h-0"
                        >
                            {Object.keys(filterData.data[key])?.map((label) => (
                                <li key={`${key}-${label}`}>
                                    <label
                                        className={clsx(
                                            "flex items-center gap-x-2 cursor-pointer",
                                            {
                                                "text-accent": Object.keys(
                                                    refinementList.filters ?? {}
                                                )?.includes(`${key}-${label}`),
                                            }
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            defaultChecked={Object.keys(
                                                refinementList.filters ?? {}
                                            )?.includes(`${key}-${label}`)}
                                            onChange={(e) =>
                                                handleFilterChange(
                                                    e,
                                                    `${key}-${label}`,
                                                    filterData.data[key][label]
                                                )
                                            }
                                            className="accent-amber-200 hidden"
                                        />
                                        {label}
                                        <BsCheck
                                            size={24}
                                            className={clsx(
                                                "absolute right-3",
                                                {
                                                    " hidden": !Object.keys(
                                                        refinementList.filters ??
                                                            {}
                                                    )?.includes(
                                                        `${key}-${label}`
                                                    ),
                                                }
                                            )}
                                        />
                                    </label>
                                </li>
                            ))}
                        </Transition>
                    </div>
                ))}
        </div>
    )
}

export default RefinementList
